import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  FormField,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
import { useMemo, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { BiasCoachBanner } from '../bias/bias-coach-banner'
import { detect, joinedText } from '../../lib/bias-guardrail'
import {
  ROLE_SENIORITY_OPTIONS,
  useCreateRole,
  useUpdateRole,
  type RoleDetail,
  type RoleSeniority,
  type RoleStatus,
} from '../../hooks/use-roles'

const FORM_ID = 'role-canvas-form'
const NO_SENIORITY = 'none'

export function RoleCanvasForm({
  role,
  onClose,
  onSaved,
}: {
  role?: RoleDetail
  onClose: () => void
  onSaved: (role: RoleDetail) => void
}) {
  const [title, setTitle] = useState(role?.title ?? '')
  const [family, setFamily] = useState(role?.family ?? '')
  const [seniority, setSeniority] = useState<RoleSeniority>(role?.seniority ?? '')
  const [department, setDepartment] = useState(role?.department ?? '')
  const [summary, setSummary] = useState(role?.summary ?? '')
  const [status, setStatus] = useState<RoleStatus>(role?.status ?? 'draft')
  const create = useCreateRole()
  const update = useUpdateRole()
  const isSaving = create.isPending || update.isPending
  const draftFindings = useMemo(
    () => detect(joinedText(title, summary), 'competency_framework'),
    [title, summary],
  )

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const fields = { title: title.trim(), family: family.trim(), seniority, department, summary, status }
    const callbacks = {
      onSuccess: onSaved,
      onError: (error: Error) => toast.error(error.message || 'Failed to save role'),
    }
    if (role) update.mutate({ id: role.id, fields }, callbacks)
    else create.mutate(fields, callbacks)
  }

  return (
    <>
      <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
        <BiasCoachBanner findings={draftFindings} source={role ? 'edit' : 'generate'} />
        <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
          <FormField label="Title *">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Senior Brick Layer"
              required
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Family" description="Groups seniority variants of the same role.">
              <Input value={family} onChange={(e) => setFamily(e.target.value)} placeholder="e.g., Brick Layer" />
            </FormField>
            <FormField label="Seniority" htmlFor={`${FORM_ID}-seniority`}>
              <Select
                value={seniority || NO_SENIORITY}
                onValueChange={(value) => setSeniority(value === NO_SENIORITY ? '' : (value as RoleSeniority))}
              >
                <SelectTrigger id={`${FORM_ID}-seniority`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SENIORITY}>Not set</SelectItem>
                  {ROLE_SENIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <FormField label="Department">
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g., Construction" />
          </FormField>
          <FormField label="Summary">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What this role is for and what good looks like…"
              rows={4}
            />
          </FormField>
          {role ? (
            <FormField label="Status" htmlFor={`${FORM_ID}-status`}>
              <Select value={status} onValueChange={(value) => setStatus(value as RoleStatus)}>
                <SelectTrigger id={`${FORM_ID}-status`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          ) : null}
        </form>
      </ArtifactCanvasBody>
      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={() => (document.getElementById(FORM_ID) as HTMLFormElement | null)?.requestSubmit()}
            isDirty
            isSaving={isSaving}
            saveLabel={role ? 'Save role' : 'Create role'}
            closeDisabled={isSaving}
          />
        }
      />
    </>
  )
}
